import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  AccountAssignedMall,
  AccountAssignedMallVariables
} from "./types/AccountAssignedMall";
import {
  ExportRemittanceOrderFile,
  ExportRemittanceOrderFileVariables
} from "./types/ExportRemittanceOrderFile";
import { GetRemittance, GetRemittanceVariables } from "./types/GetRemittance";
import {
  RemittanceRunnerMallFilterData,
  RemittanceRunnerMallFilterDataVariables
} from "./types/RemittanceRunnerMallFilterData";
import {
  RemittanceRunnerOrders,
  RemittanceRunnerOrdersVariables
} from "./types/RemittanceRunnerOrders";
import {
  RemittanceRunnerRemittances,
  RemittanceRunnerRemittancesVariables
} from "./types/RemittanceRunnerRemittances";

export const remittanceRunnerOrders = gql`
  ${pageInfoFragment}
  query RemittanceRunnerOrders(
    $search: String!
    $first: Int
    $before: String
    $after: String
    $last: Int
    $smopStatus: [SmopOrderStatusEnum]
    $paymentType: String
    $deliveryTypes: [DeliveryTypeEnum]
    $status: [RemittanceStatusEnum]
    $orderCode: String
    $shippedAt: DateTimeRangeInput
    $completedAt: DateTimeRangeInput
    $rider: RiderNameInput
    $amount: String
    $remittedAt: DateTimeRangeInput
    $remitter: RemitterNameInput
    $remittanceId: String
    $sortBy: RemittanceOrderSortingInput
    $hasRemittanceId: Boolean
    $malls: [ID]
    $isPaymentCollected: Boolean
    $createdAt: DateTimeRangeInput
    $hasReceipt: Boolean
    $companyName: [CourierTypeEnum]
    $noCollectionRequired: Boolean
  ) {
    remittanceOrders(
      first: $first
      before: $before
      after: $after
      last: $last
      filter: {
        search: $search
        smopStatus: $smopStatus
        paymentType: $paymentType
        deliveryTypes: $deliveryTypes
        status: $status
        orderCode: $orderCode
        shippedAt: $shippedAt
        completedAt: $completedAt
        rider: $rider
        amount: $amount
        remittedAt: $remittedAt
        remitter: $remitter
        remittanceId: $remittanceId
        hasRemittanceId: $hasRemittanceId
        malls: $malls
        isPaymentCollected: $isPaymentCollected
        createdAt: $createdAt
        hasReceipt: $hasReceipt
        companyName: $companyName
        noCollectionRequired: $noCollectionRequired
      }
      sortBy: $sortBy
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        node {
          noCollectionRequired
          id
          orderCode
          shippedAt
          completedAt
          smopStatus
          remittanceManualAdjustmentStatus
          remittanceManualAdjustmentAmount
          remittanceManualAdjustmentTotalAmount
          isPaymentCollected
          courier
          mall {
            name
          }
          payments {
            paymentMethodType
          }
          shippingMethod {
            deliveryType
          }
          total {
            currency
            gross {
              amount
            }
            net {
              amount
            }
            tax {
              amount
            }
          }
          rider {
            id
            firstName
            lastName
            homeAddress {
              companyName
            }
          }
          isRider3pl
          thirdPartyRider {
            id
            firstName
            lastName
            deliveryCourier {
              shippingCompany
            }
          }
          subOrders {
            tenant {
              brand {
                name
              }
            }
          }
          remittanceLine {
            remittance {
              id
              remittanceNo
              status
              remittedAt
              receiptImage {
                url
                alt
              }
              remitter {
                id
                firstName
                lastName
                homeAddress {
                  companyName
                }
              }
              totalAmount
              aging
              createdAt
              remittanceLines {
                id
                createdAt
                order {
                  id
                  parcelNo
                  smopStatus
                  orderCode
                  user {
                    accessGroup {
                      smopUsertypeCode
                    }
                  }
                  payments {
                    paymentMethodType
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
export const useRemittanceRunnerOrders = makeQuery<
  RemittanceRunnerOrders,
  RemittanceRunnerOrdersVariables
>(remittanceRunnerOrders);

export const getRemittance = gql`
  query GetRemittance($id: ID!) {
    remittance(id: $id) {
      id
      remittanceNo
      totalAmount
      remittedAt
      createdAt
      remittanceLines {
        id
        order {
          id
          orderCode
          completedAt
          shippedAt
          total {
            gross {
              amount
            }
          }
          remittanceManualAdjustmentStatus
          remittanceManualAdjustmentAmount
          remittanceManualAdjustmentTotalAmount
        }
        createdAt
      }
      remittanceEvents {
        id
        user {
          id
          email
        }
        message
        datetime
        type
      }
      status
      remitter {
        id
        firstName
        lastName
        email
        contactNo
      }
      aging
      isProcessing
      note
      receiptImage {
        url
        alt
      }
    }
  }
`;
export const useGetRemittance = makeQuery<
  GetRemittance,
  GetRemittanceVariables
>(getRemittance);

export const remittanceRunnerRemittances = gql`
  ${pageInfoFragment}
  query RemittanceRunnerRemittances(
    $first: Int
    $before: String
    $after: String
    $last: Int
    $status: [RemittanceStatusEnum]
    $search: String
    $remittedAt: DateTimeRangeInput
    $remitter: RemitterNameInput
    $sortBy: RemittanceSortingInput
    $createdAt: DateTimeRangeInput
    $remittanceId: String
    $hasReceipt: Boolean
  ) {
    remittances(
      first: $first
      before: $before
      after: $after
      last: $last
      filter: {
        status: $status
        search: $search
        remittedAt: $remittedAt
        remitter: $remitter
        createdAt: $createdAt
        remittanceId: $remittanceId
        hasReceipt: $hasReceipt
      }
      sortBy: $sortBy
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        node {
          id
          remittanceNo
          status
          remittedAt
          remitter {
            id
            firstName
            lastName
            homeAddress {
              companyName
            }
          }
          totalAmount
          aging
          createdAt
          receiptImage {
            url
            alt
          }
          remittanceLines {
            id
            createdAt
            order {
              id
              parcelNo
              smopStatus
              orderCode
              payments {
                paymentMethodType
              }
            }
          }
        }
      }
    }
  }
`;
export const useRemittanceRunnerRemittances = makeQuery<
  RemittanceRunnerRemittances,
  RemittanceRunnerRemittancesVariables
>(remittanceRunnerRemittances);

export const accountAssignedMall = gql`
  query AccountAssignedMall($id: ID!) {
    mall(id: $id) {
      id
      name
      remittanceConfig {
        isEnable
        minRemittanceLimit
      }
    }
  }
`;
export const useAccountAssignedMall = makeQuery<
  AccountAssignedMall,
  AccountAssignedMallVariables
>(accountAssignedMall);

export const remittanceRunnerMallFilterDataQuery = gql`
  query RemittanceRunnerMallFilterData($mall: [ID], $first: Int!) {
    malls(first: $first, filter: { id: $mall }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const useRemittanceRunnerMallFilterDataQuery = makeQuery<
  RemittanceRunnerMallFilterData,
  RemittanceRunnerMallFilterDataVariables
>(remittanceRunnerMallFilterDataQuery);

export const exportRemittanceOrderFile = gql`
  query ExportRemittanceOrderFile($id: ID!) {
    exportFile(id: $id) {
      id
      status
      url
    }
  }
`;

export const useExportRemittanceOrderFile = makeQuery<
  ExportRemittanceOrderFile,
  ExportRemittanceOrderFileVariables
>(exportRemittanceOrderFile);
